/**
 * Sidebar — Phase 6a.9 rebuild.
 *
 * Replaces the custom Phase 6a.2 sidebar with shadcn/ui's `<Sidebar>`
 * composition. Provides:
 *   • Branding (Logo + app name)
 *   • Primary CTA: "Yeni Engagement" → /engagements/new
 *   • Secondary nav: Geçmiş, Ayarlar
 *   • Footer: language toggle, theme toggle, version stamp
 *
 * Active route highlighting is driven by `useLocation()`. Mobile
 * responsiveness is handled by shadcn/ui's `<SidebarTrigger>` (rendered
 * by the AppLayout in the top nav).
 */

import { History, Home, Plus, Settings, ShieldCheck } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { useIsAdmin } from "@/features/auth/useIsAdmin";

import { LanguageToggle } from "./LanguageToggle";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";

const APP_VERSION = "v0.6a.9";

const SECONDARY_NAV = [
  { to: "/history", icon: History, labelKey: "nav.history" },
  { to: "/settings", icon: Settings, labelKey: "nav.settings" },
] as const;

export function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpen, isMobile } = useSidebar();
  const { isAdmin } = useIsAdmin();

  // Hover-to-expand (desktop only). On mobile the sidebar is a Sheet
  // driven by the trigger, so hover must be a no-op there.
  const handleMouseEnter = React.useCallback(() => {
    if (!isMobile) setOpen(true);
  }, [isMobile, setOpen]);
  const handleMouseLeave = React.useCallback(() => {
    if (!isMobile) setOpen(false);
  }, [isMobile, setOpen]);

  function isActive(path: string): boolean {
    if (path === "/") return location.pathname === "/";
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  }

  return (
    <SidebarRoot
      variant="inset"
      collapsible="icon"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1.5">
          <Logo />
          <span className="text-sm font-semibold group-data-[collapsible=icon]:hidden">
            {t("appName")}
          </span>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isActive("/")}
                  onClick={() => navigate("/")}
                  tooltip={t("nav.dashboard")}
                >
                  <Home />
                  <span>{t("nav.dashboard")}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isActive("/engagements/new")}
                  onClick={() => navigate("/engagements/new")}
                  tooltip={t("nav.newEngagement")}
                  className="font-medium"
                >
                  <Plus />
                  <span>{t("nav.newEngagement")}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {SECONDARY_NAV.map((item) => (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton
                    isActive={isActive(item.to)}
                    onClick={() => navigate(item.to)}
                    tooltip={t(item.labelKey)}
                  >
                    <item.icon />
                    <span>{t(item.labelKey)}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {/* SPEC-22 — admin-only entry, gated by useIsAdmin. */}
              {isAdmin ? (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={isActive("/admin")}
                    onClick={() => navigate("/admin")}
                    tooltip={t("nav.admin")}
                  >
                    <ShieldCheck />
                    <span>{t("nav.admin")}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : null}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <div className="flex items-center justify-between gap-2 px-2 py-1 group-data-[collapsible=icon]:hidden">
          <LanguageToggle />
          <ThemeToggle />
        </div>
        <div className="px-2 pb-1 text-[10px] text-muted-foreground group-data-[collapsible=icon]:hidden">
          {APP_VERSION}
        </div>
      </SidebarFooter>
    </SidebarRoot>
  );
}
